class IntroManager {
    constructor(app) {
        this.app = app;
        this.quoteOverlay = null;
        this.secondQuoteOverlay = null;
        this.skipButton = null;
        this.skipButton2 = null;
        this.isSkipped = false;
        this.eventListeners = {};
    }

    init() {
        // Create and set up overlays
        this.createOverlays();
        this.setupSkipButtons();
        
        // Hide main elements initially
        this.hideMainElements();
        
        // Start the sequence
        this.playIntroSequence().catch(error => {
            console.error('Error in intro sequence:', error);
            // If there's an error, ensure we show the main interface
            this.showMainElements();
        });
    }

    createOverlays() {
        // Create first quote overlay
        this.quoteOverlay = this.createOverlay({
            id: 'quote-overlay',
            text: '"once upon a fictional and digital time, the universe was mapped to a fabric grid of 0s and 1s"',
            background: 'black'
        });

        // Create second quote overlay
        this.secondQuoteOverlay = this.createOverlay({
            id: 'second-quote-overlay',
            text: '"somewhere in this Kosmic Kluster, the OB/AI pod came into being"',
            background: 'transparent',
            hidden: true
        });

        document.body.appendChild(this.quoteOverlay);
        document.body.appendChild(this.secondQuoteOverlay);
    }

    createOverlay({ id, text, background, hidden = false }) {
        const overlay = document.createElement('div');
        overlay.id = id;
        overlay.className = 'intro-overlay' + (hidden ? ' hidden' : '');
        overlay.style.backgroundColor = background;

        const textDiv = document.createElement('div');
        textDiv.className = 'quote-text';
        textDiv.textContent = text;
        overlay.appendChild(textDiv);

        const skipButton = document.createElement('button');
        skipButton.className = 'skip-intro-button';
        skipButton.textContent = 'Skip Intro';
        overlay.appendChild(skipButton);

        return overlay;
    }

    setupSkipButtons() {
        const skipButtons = document.querySelectorAll('.skip-intro-button');
        skipButtons.forEach(button => {
            button.addEventListener('click', () => this.skipIntro());
        });
    }

    hideMainElements() {
        const elements = ['#scene-container', 'header', '#navigation'];
        elements.forEach(selector => {
            const element = document.querySelector(selector);
            if (element) {
                element.style.opacity = '0';
                element.style.transition = 'opacity 1.5s ease-in-out';
            }
        });
    }

    showMainElements() {
        const elements = ['#scene-container', 'header', '#navigation'];
        elements.forEach(selector => {
            const element = document.querySelector(selector);
            if (element) {
                element.style.opacity = '1';
            }
        });
    }

    async fadeOut(element, duration = 1500) {
        return new Promise(resolve => {
            if (!element) {
                resolve();
                return;
            }

            element.classList.add('fade-out');
            setTimeout(() => {
                element.remove();
                resolve();
            }, duration);
        });
    }

    async fadeIn(element, duration = 1500) {
        return new Promise(resolve => {
            if (!element) {
                resolve();
                return;
            }

            element.style.opacity = '1';
            setTimeout(resolve, duration);
        });
    }

    async delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    addEventListener(event, callback) {
        if (!this.eventListeners[event]) {
            this.eventListeners[event] = [];
        }
        this.eventListeners[event].push(callback);
    }

    triggerEvent(event) {
        if (this.eventListeners[event]) {
            this.eventListeners[event].forEach(callback => callback());
        }
    }

    async skipIntro() {
        if (this.isSkipped) return;
        this.isSkipped = true;

        // Remove overlays
        this.quoteOverlay?.remove();
        this.secondQuoteOverlay?.remove();

        // Show main elements
        this.showMainElements();

        // Fade in pod quickly
        if (this.app.podManager?.pod) {
            await this.app.podManager.pod.fadeIn(1000);
        }

        // Make pod clickable
        if (this.app.speechBubbleManager) {
            this.app.speechBubbleManager.makePodClickable();
        }

        // Emit complete event when skipped
        this.triggerEvent('complete');
    }

    async playIntroSequence() {
        if (this.isSkipped) return;

        try {
            // Step 1: Show first quote for 3.5 seconds
            await this.delay(5000);
            if (this.isSkipped) return;

            // Step 2: Fade out first quote while fading in scene and header
            await Promise.all([
                this.fadeOut(this.quoteOverlay),
                this.fadeIn(document.querySelector('#scene-container')),
                this.fadeIn(document.querySelector('header'))
            ]);
            if (this.isSkipped) return;

            // Step 3: Show second quote
            this.secondQuoteOverlay.classList.remove('hidden');
            await this.delay(4000);
            if (this.isSkipped) return;

            // Step 4: Fade out second quote
            await this.fadeOut(this.secondQuoteOverlay);
            if (this.isSkipped) return;

            // Step 5: Fade in pod
            if (this.app.podManager?.pod) {
                await this.app.podManager.pod.fadeIn(1500);
            }
            if (this.isSkipped) return;

            // Step 6: Fade in navigation
            await this.delay(1000);
            await this.fadeIn(document.querySelector('#navigation'));
            if (this.isSkipped) return;

            // Step 7: Make pod clickable
            await this.delay(1000);
            if (this.app.speechBubbleManager) {
                this.app.speechBubbleManager.makePodClickable();
            }

            // Emit complete event when sequence finishes
            this.triggerEvent('complete');
        } catch (error) {
            console.error('Error in intro sequence:', error);
            this.showMainElements();
            // Emit complete event even if there's an error
            this.triggerEvent('complete');
        }
    }
}

export { IntroManager };
